/**
 * DashboardQueryProvider — TanStack Query client for the dashboard surface.
 *
 * Plan 01-14 Task 1.  Mounted in App.tsx above the router so every data-tile
 * useQuery call (weather, sun, future chores/events/points) shares one client.
 *
 * Defaults rationale:
 *  - staleTime 30s — dashboard data is low-rate and we want minimal refetches
 *    while the clock ticks. Individual queries override (weather 60s, sun 15m).
 *  - refetchOnWindowFocus false — kitchen tablet never loses focus in a way
 *    that a window-focus refetch should trigger. Phones are fine too because
 *    TanStack Query handles `visibilityState` transitions separately.
 *  - retry 1 — flaky home networks deserve one retry but not a storm.
 */
import type { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const client = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function DashboardQueryProvider({ children }: { children: ReactNode }) {
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
